const QUILL_SIZE_STYLE_MAP = {
    small: '0.75em',
    normal: '1em',
    large: '1.5em',
    huge: '2.5em',
};

export const prepareQuillHtmlForEmail = (html = '') => {
    const trimmed = (html || '').trim();
    if (!trimmed) return '';

    if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
        return trimmed;
    }

    const parser = new window.DOMParser();
    const doc = parser.parseFromString(`<div>${trimmed}</div>`, 'text/html');
    const root = doc.body.firstElementChild;

    if (!root) return trimmed;

    Object.entries(QUILL_SIZE_STYLE_MAP).forEach(([sizeKey, fontSize]) => {
        const nodes = root.querySelectorAll(`.ql-size-${sizeKey}`);

        nodes.forEach((node) => {
            node.style.fontSize = fontSize;
        });
    });

    // Convert Quill alignment classes to inline `text-align` for email clients.
    const ALIGN_CLASS_TO_VALUE = {
        'ql-align-center': 'center',
        'ql-align-right': 'right',
        'ql-align-justify': 'justify',
        'ql-align-left': 'left',
    };

    Object.entries(ALIGN_CLASS_TO_VALUE).forEach(([alignClass, textAlign]) => {
        root.querySelectorAll(`.${alignClass}`).forEach((node) => {
            node.style.textAlign = textAlign;
        });
    });

    return root.innerHTML.trim();
};

export const prepareEmailHtmlForQuill = (html = '') => {
    const trimmed = (html || '').trim();
    if (!trimmed) return '';

    if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
        return trimmed;
    }

    const parser = new window.DOMParser();
    const doc = parser.parseFromString(`<div>${trimmed}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) return trimmed;

    const FONT_SIZE_TO_QUILL_KEY = {
        // Current em mapping
        '0.75em': 'small',
        '1em': 'normal',
        '1.5em': 'large',
        '2.5em': 'huge',

        // Backward compatibility (pt mapping)
        '10pt': 'small',
        '12pt': 'normal',
        '14pt': 'large',
        '18pt': 'huge',
        '16pt': 'large',
        '20pt': 'huge',
    };

    const TEXT_ALIGN_TO_QL_CLASS = {
        center: 'ql-align-center',
        right: 'ql-align-right',
        justify: 'ql-align-justify',
        left: 'ql-align-left',
    };

    root.querySelectorAll('[style]').forEach((node) => {
        const styleAttr = node.getAttribute('style') || '';

        const match = styleAttr.match(/font-size\s*:\s*([^;]+)\s*;?/i);
        const alignMatch = styleAttr.match(/text-align\s*:\s*([^;]+)\s*;?/i);

        // Convert font-size style to `ql-size-*` class.
        if (match) {
            const raw = (match[1] || '').trim().toLowerCase();
            const directKey = FONT_SIZE_TO_QUILL_KEY[raw];

            // Normalize values like `1em` / `1.0em` or `0.75em` with extra spaces.
            const emNumMatch = raw.match(/^([0-9.]+)\s*em$/i);
            const ptNumMatch = raw.match(/^([0-9.]+)\s*pt$/i);

            let key = directKey;
            if (!key && emNumMatch) {
                const n = Number(emNumMatch[1]);
                if (n === 0.75) key = 'small';
                if (n === 1) key = 'normal';
                if (n === 1.5) key = 'large';
                if (n === 2.5) key = 'huge';
            }
            if (!key && ptNumMatch) {
                const n = String(ptNumMatch[1]) + 'pt';
                key = FONT_SIZE_TO_QUILL_KEY[n];
            }

            if (key) {
                // Quill represents "normal" as no size class.
                if (key === 'normal') {
                    node.classList.remove('ql-size-small', 'ql-size-normal', 'ql-size-large', 'ql-size-huge');
                } else {
                    node.classList.add(`ql-size-${key}`);
                }

                node.style.fontSize = '';
            }
        }

        // Convert alignment style to `ql-align-*` class.
        if (alignMatch) {
            const rawAlign = (alignMatch[1] || '').trim().toLowerCase();
            const qlAlignClass = TEXT_ALIGN_TO_QL_CLASS[rawAlign];
            if (qlAlignClass) {
                node.classList.add(qlAlignClass);
                node.style.textAlign = '';
            }
        }

        // If the element style is now empty, remove it to avoid any interference.
        if (!(node.getAttribute('style') || '').trim()) {
            node.removeAttribute('style');
        }
    });

    return root.innerHTML.trim();
};
